import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useList } from '@/hooks/resourceQueries';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { TimeInput } from '@/components/ui/time-input';
import { Label } from '@/components/ui/label';
import { PeriodInput } from '@/components/ui/period-input';
import { SentryDropdown } from '@/components/ui/sentry-dropdown';
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger
} from '@/components/ui/collapsible';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle
} from '@/components/ui/dialog';
import {
	Calendar,
	Clock,
	Users,
	Car,
	Building,
	Plus,
	Save,
	AlertCircle,
	ChevronDown,
	ChevronRight,
	ChevronsUpDown,
	X
} from 'lucide-react';
import {
	VEHICLE_TYPES_QUERY,
	CUSTOMER_CATEGORIES_QUERY,
	WEEKLY_DATE_SETS_QUERY,
	PAYMENT_METHODS_QUERY
} from './scenarios.gql';
import type {
	VehicleTypeViewModel,
	CustomerCategoryViewModel,
	WeeklyDateSetViewModel,
	PaymentMethodViewModel
} from '@/graphql/generated';
import { validateConditionHasActions } from '@/lib/conditionValidation';
import { toast } from 'sonner';

interface ConditionFormData {
	startDate?: string;
	endDate?: string;
	arrivingSince?: string;
	arrivingUntil?: string;
	leavingSince?: string;
	leavingUntil?: string;
	weeklyDateSetId?: number;
	appliesOnHolidays: boolean;
	minStayMinutes?: number;
	maxStayMinutes?: number;
	appliesToSpecificVehicleTypes: boolean;
	appliesToSpecificCustomerCategories: boolean;
	appliesToSpecificPaymentMethods: boolean;
	isOverstay: boolean;
	isContractOverstay: boolean;
	selectedVehicleTypes: string[];
	selectedCustomerCategories: string[];
	selectedPaymentMethods: string[];
	selectedDaysOfWeek: string[];
}

interface ConditionEditorProps {
	condition?: ConditionFormData;
	operator: 'include' | 'exclude';
	onSave: (
		condition: ConditionFormData & { operator: 'include' | 'exclude' }
	) => void;
	onCancel: () => void;
	open: boolean;
	mode: 'create' | 'edit';
}

type RuleSectionId = 'basic' | 'arriving' | 'leaving' | 'datePeriod' | 'weeklyDates' | 'stay' | 'vehicleTypes' | 'customerCategories' | 'paymentMethods';

// Days of week will be translated in the component using t()
const DAYS_OF_WEEK_IDS = [
	{ id: '1', key: 'monday' },
	{ id: '2', key: 'tuesday' },
	{ id: '3', key: 'wednesday' },
	{ id: '4', key: 'thursday' },
	{ id: '5', key: 'friday' },
	{ id: '6', key: 'saturday' },
	{ id: '7', key: 'sunday' }
];

function detectSectionsWithData(condition: ConditionFormData): RuleSectionId[] {
	const result: RuleSectionId[] = [];
	if (condition.appliesOnHolidays || condition.isOverstay || condition.isContractOverstay) {
		result.push('basic');
	}
	if (condition.arrivingSince || condition.arrivingUntil) {
		result.push('arriving');
	}
	if (condition.leavingSince || condition.leavingUntil) {
		result.push('leaving');
	}
	if (condition.startDate || condition.endDate) {
		result.push('datePeriod');
	}
	if (condition.weeklyDateSetId || (condition.selectedDaysOfWeek && condition.selectedDaysOfWeek.length > 0)) {
		result.push('weeklyDates');
	}
	if (condition.minStayMinutes || condition.maxStayMinutes) {
		result.push('stay');
	}
	if (condition.selectedVehicleTypes && condition.selectedVehicleTypes.length > 0) {
		result.push('vehicleTypes');
	}
	if (condition.selectedCustomerCategories && condition.selectedCustomerCategories.length > 0) {
		result.push('customerCategories');
	}
	if (condition.selectedPaymentMethods && condition.selectedPaymentMethods.length > 0) {
		result.push('paymentMethods');
	}
	return result;
}

const TAG_CLASS_SELECTED = 'bg-primary text-primary-foreground border-primary font-semibold';
const TAG_CLASS_UNSELECTED = 'bg-background text-muted-foreground border-border hover:bg-muted/50';

export const ConditionEditor: React.FC<ConditionEditorProps> = ({
	condition,
	operator,
	onSave,
	onCancel,
	open,
	mode
}) => {
	const { t } = useTranslation('scenarios');
	const [addedSections, setAddedSections] = useState<RuleSectionId[]>([]);
	const [collapsedSections, setCollapsedSections] = useState<Set<RuleSectionId>>(new Set());
	const [addMenuOpen, setAddMenuOpen] = useState(false);
	const addMenuRef = useRef<HTMLDivElement>(null);
	const scrollAreaRef = useRef<HTMLDivElement>(null);

	// Close menu on outside click
	useEffect(() => {
		if (!addMenuOpen) return;
		const handleClick = (e: MouseEvent) => {
			if (addMenuRef.current && !addMenuRef.current.contains(e.target as Node)) {
				setAddMenuOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClick);
		return () => document.removeEventListener('mousedown', handleClick);
	}, [addMenuOpen]);

	const { control, handleSubmit, reset, watch, setValue, setError, formState: { errors } } =
		useForm<ConditionFormData>({
			defaultValues: {
				appliesOnHolidays: false,
				appliesToSpecificVehicleTypes: false,
				appliesToSpecificCustomerCategories: false,
				appliesToSpecificPaymentMethods: false,
				isOverstay: false,
				isContractOverstay: false,
				selectedVehicleTypes: [],
				selectedCustomerCategories: [],
				selectedPaymentMethods: [],
				selectedDaysOfWeek: []
			}
		});

	const sections: { id: RuleSectionId; label: string; icon: React.FC<{ className?: string }>; group: string }[] = [
		{ id: 'basic', label: t('conditionEditor.generalRulesTab'), icon: Building, group: 'general' },
		{ id: 'arriving', label: t('conditionEditor.arrivingTimeTab'), icon: Clock, group: 'timeDate' },
		{ id: 'leaving', label: t('conditionEditor.leavingTimeTab'), icon: Clock, group: 'timeDate' },
		{ id: 'datePeriod', label: t('conditionEditor.datePeriodTab'), icon: Calendar, group: 'timeDate' },
		{ id: 'weeklyDates', label: t('conditionEditor.weeklyDatesTab'), icon: Calendar, group: 'timeDate' },
		{ id: 'stay', label: t('conditionEditor.stayRulesTab'), icon: AlertCircle, group: 'durationRestrictions' },
		{ id: 'vehicleTypes', label: t('conditionEditor.vehicleTypesTab'), icon: Car, group: 'restrictions' },
		{ id: 'customerCategories', label: t('conditionEditor.customerCategoriesTab'), icon: Users, group: 'restrictions' },
		{ id: 'paymentMethods', label: t('conditionEditor.paymentMethodsTab'), icon: Building, group: 'restrictions' }
	];

	const availableSections = sections.filter(s => !addedSections.includes(s.id));

	const addSection = useCallback((id: RuleSectionId) => {
		setAddedSections(prev => prev.includes(id) ? prev : [...prev, id]);
		setCollapsedSections(prev => {
			const next = new Set(prev);
			next.delete(id);
			return next;
		});
		setAddMenuOpen(false);
		// Scroll to bottom after React renders the new section
		setTimeout(() => {
			scrollAreaRef.current?.scrollTo({ top: scrollAreaRef.current.scrollHeight, behavior: 'smooth' });
		}, 50);
	}, []);

	const removeSection = useCallback((id: RuleSectionId) => {
		setAddedSections(prev => prev.filter(s => s !== id));
		// Clear form values for removed section
		switch (id) {
			case 'basic':
				setValue('appliesOnHolidays', false);
				setValue('isOverstay', false);
				setValue('isContractOverstay', false);
				break;
			case 'arriving':
				setValue('arrivingSince', undefined);
				setValue('arrivingUntil', undefined);
				break;
			case 'leaving':
				setValue('leavingSince', undefined);
				setValue('leavingUntil', undefined);
				break;
			case 'datePeriod':
				setValue('startDate', undefined);
				setValue('endDate', undefined);
				break;
			case 'weeklyDates':
				setValue('weeklyDateSetId', undefined);
				setValue('selectedDaysOfWeek', []);
				break;
			case 'stay':
				setValue('minStayMinutes', undefined);
				setValue('maxStayMinutes', undefined);
				break;
			case 'vehicleTypes':
				setValue('selectedVehicleTypes', []);
				setValue('appliesToSpecificVehicleTypes', false);
				break;
			case 'customerCategories':
				setValue('selectedCustomerCategories', []);
				setValue('appliesToSpecificCustomerCategories', false);
				break;
			case 'paymentMethods':
				setValue('selectedPaymentMethods', []);
				setValue('appliesToSpecificPaymentMethods', false);
				break;
		}
	}, [setValue]);

	const toggleCollapse = useCallback((id: RuleSectionId) => {
		setCollapsedSections(prev => {
			const next = new Set(prev);
			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}
			return next;
		});
	}, []);

	const allCollapsed = addedSections.length > 0 && addedSections.every(id => collapsedSections.has(id));

	const toggleCollapseAll = useCallback(() => {
		if (allCollapsed) {
			setCollapsedSections(new Set());
		} else {
			setCollapsedSections(new Set(addedSections));
		}
	}, [allCollapsed, addedSections]);

	// Load data for dropdowns
	const { data: vehicleTypesData } = useList<VehicleTypeViewModel>({
		resource: 'vehicleTypes',
		meta: {
			gqlQuery: VEHICLE_TYPES_QUERY
		}
	});

	const { data: customerCategoriesData } = useList<CustomerCategoryViewModel>(
		{
			resource: 'customerCategories',
			meta: {
				gqlQuery: CUSTOMER_CATEGORIES_QUERY
			}
		}
	);

	const { data: weeklyDateSetsData } = useList<WeeklyDateSetViewModel>({
		resource: 'weeklyDateSets',
		meta: {
			gqlQuery: WEEKLY_DATE_SETS_QUERY
		}
	});

	const { data: paymentMethodsData } = useList<PaymentMethodViewModel>({
		resource: 'paymentMethods',
		meta: {
			gqlQuery: PAYMENT_METHODS_QUERY
		}
	});

	const vehicleTypes = vehicleTypesData?.data || [];
	const customerCategories = customerCategoriesData?.data || [];
	const weeklyDateSets = weeklyDateSetsData?.data || [];
	const paymentMethods = paymentMethodsData?.data || [];

	const weeklyDateSetId = watch('weeklyDateSetId');
	const selectedWeeklyDateSet = weeklyDateSets.find(
		set => set.weeklyDateSetId === weeklyDateSetId
	);
	const showCustomWeekdays =
		selectedWeeklyDateSet?.name?.toLowerCase() === 'custom';

	const selectedDaysOfWeek = watch('selectedDaysOfWeek');
	const selectedVehicleTypes = watch('selectedVehicleTypes');
	const selectedCustomerCategories = watch('selectedCustomerCategories');
	const selectedPaymentMethods = watch('selectedPaymentMethods');

	const getSectionCount = useCallback((sectionId: RuleSectionId): number | undefined => {
		switch (sectionId) {
			case 'vehicleTypes': return selectedVehicleTypes.length || undefined;
			case 'customerCategories': return selectedCustomerCategories.length || undefined;
			case 'paymentMethods': return selectedPaymentMethods.length || undefined;
			case 'weeklyDates': return selectedDaysOfWeek.length || undefined;
			default: return undefined;
		}
	}, [selectedVehicleTypes, selectedCustomerCategories, selectedPaymentMethods, selectedDaysOfWeek]);

	useEffect(() => {
		if (weeklyDateSetId != null || weeklyDateSets.length === 0) {
			return;
		}

		const normaliseName = (value?: string | null) =>
			value?.replace(/\s+/g, '').toLowerCase();

		const defaultWeeklySet =
			weeklyDateSets.find(set => normaliseName(set.name) === 'alldays') ??
			weeklyDateSets[0];

		if (defaultWeeklySet) {
			setValue('weeklyDateSetId', defaultWeeklySet.weeklyDateSetId);
		}
	}, [weeklyDateSetId, weeklyDateSets, setValue]);

	useEffect(() => {
		if (!showCustomWeekdays && selectedDaysOfWeek.length > 0) {
			setValue('selectedDaysOfWeek', []);
		}
	}, [showCustomWeekdays, selectedDaysOfWeek, setValue]);

	// Populate form when editing existing condition or reset for new condition
	useEffect(() => {
		if (open) {
			if (condition && mode === 'edit') {
				reset({
					startDate: condition.startDate
						? new Date(condition.startDate)
								.toISOString()
								.split('T')[0]
						: undefined,
					endDate: condition.endDate
						? new Date(condition.endDate)
								.toISOString()
								.split('T')[0]
						: undefined,
					arrivingSince: condition.arrivingSince || undefined,
					arrivingUntil: condition.arrivingUntil || undefined,
					leavingSince: condition.leavingSince || undefined,
					leavingUntil: condition.leavingUntil || undefined,
					weeklyDateSetId: condition.weeklyDateSetId || undefined,
					appliesOnHolidays: condition.appliesOnHolidays || false,
					minStayMinutes: condition.minStayMinutes || undefined,
					maxStayMinutes: condition.maxStayMinutes || undefined,
					appliesToSpecificVehicleTypes:
						condition.appliesToSpecificVehicleTypes || false,
					appliesToSpecificCustomerCategories:
						condition.appliesToSpecificCustomerCategories || false,
					appliesToSpecificPaymentMethods:
						condition.appliesToSpecificPaymentMethods || false,
					isOverstay: condition.isOverstay || false,
					isContractOverstay: condition.isContractOverstay || false,
					selectedVehicleTypes: condition.selectedVehicleTypes || [],
					selectedCustomerCategories:
						condition.selectedCustomerCategories || [],
					selectedPaymentMethods:
						condition.selectedPaymentMethods || [],
					selectedDaysOfWeek: condition.selectedDaysOfWeek || []
				});
				// Auto-detect which sections have data and show them
				setAddedSections(detectSectionsWithData(condition));
				setCollapsedSections(new Set());
				setAddMenuOpen(false);
			} else if (mode === 'create') {
				reset({
					appliesOnHolidays: false,
					appliesToSpecificVehicleTypes: false,
					appliesToSpecificCustomerCategories: false,
					appliesToSpecificPaymentMethods: false,
					isOverstay: false,
					isContractOverstay: false,
					selectedVehicleTypes: [],
					selectedCustomerCategories: [],
					selectedPaymentMethods: [],
					selectedDaysOfWeek: []
				});
				setAddedSections([]);
				setCollapsedSections(new Set());
				setAddMenuOpen(false);
			}
		}
	}, [open, condition, mode, reset]);

	const onSubmit = (data: ConditionFormData) => {
		// Validate that minStayMinutes is less than maxStayMinutes when both are set
		if (data.minStayMinutes && data.maxStayMinutes && data.minStayMinutes >= data.maxStayMinutes) {
			setError('minStayMinutes', {
				type: 'manual',
				message: t('conditionEditor.minStayMustBeLessThanMax')
			});
			// Ensure stay section is visible and expanded
			if (!addedSections.includes('stay')) {
				addSection('stay');
			}
			setCollapsedSections(prev => {
				const next = new Set(prev);
				next.delete('stay');
				return next;
			});
			return;
		}

		// Automatically set the "applies to specific" flags based on selections
		const selectedWeeklySet = weeklyDateSets.find(
			set => set.weeklyDateSetId === data.weeklyDateSetId
		);
		const processedData = {
			...data,
			operator,
			weeklyDateSetName: selectedWeeklySet?.name || undefined,
			// Auto-set flags based on whether any items are selected
			appliesToSpecificVehicleTypes: data.selectedVehicleTypes.length > 0,
			appliesToSpecificCustomerCategories:
				data.selectedCustomerCategories.length > 0,
			appliesToSpecificPaymentMethods:
				data.selectedPaymentMethods.length > 0
		};

		// Validate that the condition has meaningful actions
		if (!validateConditionHasActions(processedData)) {
			toast.error(t('conditionEditor.validationError'), {
				duration: 5000
			});
			return;
		}

		onSave(processedData);
	};

	// Order of added sections follows the original order
	const orderedAddedSections = addedSections.map(id => sections.find(s => s.id === id)!).filter(Boolean);

	const renderSectionContent = (sectionId: RuleSectionId) => {
		switch (sectionId) {
			case 'basic':
				return (
					<div className="flex flex-wrap gap-2">
						<Controller
							name="appliesOnHolidays"
							control={control}
							render={({ field }) => (
								<button
									type="button"
									onClick={() => field.onChange(!field.value)}
									className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${field.value ? TAG_CLASS_SELECTED : TAG_CLASS_UNSELECTED}`}
								>
									{t('conditionEditor.appliesOnHolidays')}
								</button>
							)}
						/>
						<Controller
							name="isOverstay"
							control={control}
							render={({ field }) => (
								<button
									type="button"
									onClick={() => field.onChange(!field.value)}
									className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${field.value ? TAG_CLASS_SELECTED : TAG_CLASS_UNSELECTED}`}
								>
									{t('conditionEditor.isOverstayCondition')}
								</button>
							)}
						/>
						<Controller
							name="isContractOverstay"
							control={control}
							render={({ field }) => (
								<button
									type="button"
									onClick={() => field.onChange(!field.value)}
									className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${field.value ? TAG_CLASS_SELECTED : TAG_CLASS_UNSELECTED}`}
								>
									{t('conditionEditor.isContractOverstay')}
								</button>
							)}
						/>
					</div>
				);

			case 'arriving':
				return (
					<div className="grid grid-cols-2 gap-4">
						<div className="space-y-2">
							<Label htmlFor="arrivingSince">
								{t('conditionEditor.arrivingSince')}
							</Label>
							<Controller
								name="arrivingSince"
								control={control}
								render={({ field }) => (
									<TimeInput
										value={field.value}
										onChange={field.onChange}
										onBlur={field.onBlur}
										id="arrivingSince"
									/>
								)}
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="arrivingUntil">
								{t('conditionEditor.arrivingUntil')}
							</Label>
							<Controller
								name="arrivingUntil"
								control={control}
								render={({ field }) => (
									<TimeInput
										value={field.value}
										onChange={field.onChange}
										onBlur={field.onBlur}
										id="arrivingUntil"
									/>
								)}
							/>
						</div>
					</div>
				);

			case 'leaving':
				return (
					<div className="grid grid-cols-2 gap-4">
						<div className="space-y-2">
							<Label htmlFor="leavingSince">
								{t('conditionEditor.leavingSince')}
							</Label>
							<Controller
								name="leavingSince"
								control={control}
								render={({ field }) => (
									<TimeInput
										value={field.value}
										onChange={field.onChange}
										onBlur={field.onBlur}
										id="leavingSince"
									/>
								)}
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="leavingUntil">
								{t('conditionEditor.leavingUntil')}
							</Label>
							<Controller
								name="leavingUntil"
								control={control}
								render={({ field }) => (
									<TimeInput
										value={field.value}
										onChange={field.onChange}
										onBlur={field.onBlur}
										id="leavingUntil"
									/>
								)}
							/>
						</div>
					</div>
				);

			case 'datePeriod':
				return (
					<div className="grid grid-cols-2 gap-4">
						<div className="space-y-2">
							<Label htmlFor="startDate">
								{t('conditionEditor.startDate')}
							</Label>
							<Controller
								name="startDate"
								control={control}
								render={({ field }) => (
									<Input
										{...field}
										id="startDate"
										type="date"
									/>
								)}
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="endDate">
								{t('conditionEditor.endDate')}
							</Label>
							<Controller
								name="endDate"
								control={control}
								render={({ field }) => (
									<Input
										{...field}
										id="endDate"
										type="date"
									/>
								)}
							/>
						</div>
					</div>
				);

			case 'weeklyDates':
				return (
					<div className="space-y-4">
						<div className="space-y-2">
							<Label htmlFor="weeklyDateSetId">
								{t('conditionEditor.weeklyDateSet')}
							</Label>
							<Controller
								name="weeklyDateSetId"
								control={control}
								render={({ field }) => (
									<SentryDropdown
										options={weeklyDateSets.map(
											wds => ({
												value: wds.weeklyDateSetId.toString(),
												label: wds.name
											})
										)}
										value={
											field.value?.toString() ||
											''
										}
										onChange={value =>
											field.onChange(
												parseInt(
													value as string
												)
											)
										}
										placeholder={t('conditionEditor.selectWeeklySet')}
										searchable
										className="w-full"
									/>
								)}
							/>
						</div>

						{showCustomWeekdays && (
							<div className="space-y-3">
								<Label>
									{t('conditionEditor.daysOfWeek')}
								</Label>
								<div className="flex flex-wrap gap-2">
									{DAYS_OF_WEEK_IDS.map(
										day => (
											<Controller
												key={day.id}
												name="selectedDaysOfWeek"
												control={control}
												render={({ field }) => {
													const isChecked = field.value.includes(day.id);
													return (
														<button
															type="button"
															onClick={() => {
																if (isChecked) {
																	field.onChange(field.value.filter(id => id !== day.id));
																} else {
																	field.onChange([...field.value, day.id]);
																}
															}}
															className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${isChecked ? TAG_CLASS_SELECTED : TAG_CLASS_UNSELECTED}`}
														>
															{t(`conditionEditor.${day.key}`).slice(0, 3)}
														</button>
													);
												}}
											/>
										)
									)}
								</div>
							</div>
						)}
					</div>
				);

			case 'stay':
				return (
					<div className="space-y-4">
						<div className="grid grid-cols-2 gap-4">
							<div className="space-y-2">
								<Label htmlFor="minStayMinutes">
									{t('conditionEditor.minimumStay')}
								</Label>
								<Controller
									name="minStayMinutes"
									control={control}
									render={({ field }) => (
										<PeriodInput
											value={field.value}
											onChange={field.onChange}
											aria-invalid={!!errors.minStayMinutes}
										/>
									)}
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="maxStayMinutes">
									{t('conditionEditor.maximumStay')}
								</Label>
								<Controller
									name="maxStayMinutes"
									control={control}
									render={({ field }) => (
										<PeriodInput
											value={field.value}
											onChange={field.onChange}
										/>
									)}
								/>
							</div>
						</div>
						{errors.minStayMinutes && (
							<p className="text-sm text-destructive">
								{errors.minStayMinutes.message}
							</p>
						)}
					</div>
				);

			case 'vehicleTypes':
				return (
					<div className="flex flex-wrap gap-2">
						{vehicleTypes.map(vt => (
							<Controller
								key={vt.vehicleTypeId}
								name="selectedVehicleTypes"
								control={control}
								render={({ field }) => {
									const isChecked = field.value.includes(vt.vehicleTypeId.toString());
									return (
										<button
											type="button"
											onClick={() => {
												if (isChecked) {
													field.onChange(field.value.filter(id => id !== vt.vehicleTypeId.toString()));
												} else {
													field.onChange([...field.value, vt.vehicleTypeId.toString()]);
												}
											}}
											className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${isChecked ? TAG_CLASS_SELECTED : TAG_CLASS_UNSELECTED}`}
										>
											{vt.name}
										</button>
									);
								}}
							/>
						))}
					</div>
				);

			case 'customerCategories':
				return (
					<div className="flex flex-wrap gap-2">
						{customerCategories.map(cc => (
							<Controller
								key={cc.customerCategoryId}
								name="selectedCustomerCategories"
								control={control}
								render={({ field }) => {
									const isChecked = field.value.includes(cc.customerCategoryId.toString());
									return (
										<button
											type="button"
											onClick={() => {
												if (isChecked) {
													field.onChange(field.value.filter(id => id !== cc.customerCategoryId.toString()));
												} else {
													field.onChange([...field.value, cc.customerCategoryId.toString()]);
												}
											}}
											className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${isChecked ? TAG_CLASS_SELECTED : TAG_CLASS_UNSELECTED}`}
										>
											{cc.description}
										</button>
									);
								}}
							/>
						))}
					</div>
				);

			case 'paymentMethods':
				return (
					<div className="flex flex-wrap gap-2">
						{paymentMethods.map(pm => (
							<Controller
								key={pm.paymentMethodId}
								name="selectedPaymentMethods"
								control={control}
								render={({ field }) => {
									const isChecked = field.value.includes(pm.paymentMethodId.toString());
									return (
										<button
											type="button"
											onClick={() => {
												if (isChecked) {
													field.onChange(field.value.filter(id => id !== pm.paymentMethodId.toString()));
												} else {
													field.onChange([...field.value, pm.paymentMethodId.toString()]);
												}
											}}
											className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${isChecked ? TAG_CLASS_SELECTED : TAG_CLASS_UNSELECTED}`}
										>
											{pm.name}
										</button>
									);
								}}
							/>
						))}
					</div>
				);
		}
	};

	return (
		<Dialog
			open={open}
			onOpenChange={isOpen => {
				if (!isOpen) onCancel();
			}}
		>
			<DialogContent
				className="sm:w-[80vw] sm:max-w-[900px] sm:min-h-[70vh] sm:max-h-[90vh] overflow-hidden !flex !flex-col p-0 sm:p-0"
				enablePreRender={true}
			>
				<form
					onSubmit={handleSubmit(onSubmit)}
					className="flex flex-col flex-1 min-h-0"
				>
					<DialogHeader className="flex-shrink-0 px-6 pt-6 pb-2">
						<div>
							<DialogTitle>
								{t('conditionEditor.configureTitle')}
							</DialogTitle>
							<DialogDescription>
								{t('conditionEditor.configureDesc')}
							</DialogDescription>
						</div>
					</DialogHeader>

					<div className="flex flex-col flex-1 min-h-0 px-6 overflow-hidden">
						{/* Rule Sections */}
						<div className="flex-1 min-h-0 pt-4 overflow-y-auto" ref={scrollAreaRef}>
							<div className="pb-6 space-y-3">
								{orderedAddedSections.length === 0 && (
									<div className="flex flex-col items-center justify-center py-12 text-center">
										<Button
											type="button"
											variant="outline"
											size="lg"
											className="mb-4 rounded-full w-14 h-14"
											onClick={() => setAddMenuOpen(prev => !prev)}
										>
											<Plus className="w-6 h-6" />
										</Button>
										<p className="text-sm font-medium text-muted-foreground">
											{t('conditionEditor.noRulesAdded')}
										</p>
										<p className="max-w-sm mt-1 text-xs text-muted-foreground/70">
											{t('conditionEditor.noRulesAddedDesc')}
										</p>
									</div>
								)}

								{orderedAddedSections.map(section => {
									const Icon = section.icon;
									const isCollapsed = collapsedSections.has(section.id);
									const count = getSectionCount(section.id);

									return (
										<Collapsible
											key={section.id}
											open={!isCollapsed}
											onOpenChange={() => toggleCollapse(section.id)}
										>
											<CollapsibleTrigger asChild>
												<div className="flex items-center justify-between px-4 py-2.5 cursor-pointer select-none group hover:bg-muted/50 rounded-lg transition-colors">
													<div className="flex items-center gap-2">
														{isCollapsed ? (
															<ChevronRight className="w-4 h-4 text-muted-foreground" />
														) : (
															<ChevronDown className="w-4 h-4 text-muted-foreground" />
														)}
														<Icon className="w-4 h-4" />
														<span className="text-sm font-medium">
															{section.label}
														</span>
														{count != null && (
															<span className="inline-flex items-center justify-center h-5 min-w-5 px-1.5 rounded-full bg-primary/10 text-primary text-xs font-medium">
																{count}
															</span>
														)}
													</div>
													<Button
														type="button"
														variant="ghost"
														size="sm"
														className="h-7 px-2 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
														onClick={(e) => {
															e.stopPropagation();
															removeSection(section.id);
														}}
													>
														<X className="w-3.5 h-3.5 mr-1" />
														<span className="text-xs">{t('conditionEditor.removeRule')}</span>
													</Button>
												</div>
											</CollapsibleTrigger>
											<CollapsibleContent>
												<div className="px-4 pt-3 pb-4">
													{renderSectionContent(section.id)}
												</div>
											</CollapsibleContent>
										</Collapsible>
									);
								})}

							</div>
						</div>
					</div>

					<div className="flex items-center justify-between flex-shrink-0 px-6 pt-4 pb-6 mt-auto border-t">
						<div className="flex items-center gap-2">
							{availableSections.length > 0 && (
								<div className="relative" ref={addMenuRef}>
									<Button
										type="button"
										variant="outline"
										size="sm"
										onClick={() => setAddMenuOpen(prev => !prev)}
									>
										<Plus className="w-4 h-4 mr-2" />
										{t('conditionEditor.addRule')}
									</Button>
									{addMenuOpen && (
										<div className="absolute left-0 z-50 w-56 p-1 border rounded-md shadow-md bottom-full mb-1 bg-popover text-popover-foreground">
											{/* General group */}
											{availableSections.some(s => s.group === 'general') && (
												<div>
													<div className="px-2 py-1.5 text-sm font-medium">{t('conditionEditor.groupGeneral')}</div>
													{availableSections
														.filter(s => s.group === 'general')
														.map(section => {
															const Icon = section.icon;
															return (
																<button
																	key={section.id}
																	type="button"
																	className="flex items-center w-full gap-2 px-2 py-1.5 text-sm rounded-sm cursor-default select-none hover:bg-accent hover:text-accent-foreground"
																	onClick={() => addSection(section.id)}
																>
																	<Icon className="w-4 h-4" />
																	{section.label}
																</button>
															);
														})}
												</div>
											)}
											{/* Time & Date group */}
											{availableSections.some(s => s.group === 'timeDate') && (
												<div>
													{availableSections.some(s => s.group === 'general') && <div className="-mx-1 my-1 h-px bg-border" />}
													<div className="px-2 py-1.5 text-sm font-medium">{t('conditionEditor.groupTimeDate')}</div>
													{availableSections
														.filter(s => s.group === 'timeDate')
														.map(section => {
															const Icon = section.icon;
															return (
																<button
																	key={section.id}
																	type="button"
																	className="flex items-center w-full gap-2 px-2 py-1.5 text-sm rounded-sm cursor-default select-none hover:bg-accent hover:text-accent-foreground"
																	onClick={() => addSection(section.id)}
																>
																	<Icon className="w-4 h-4" />
																	{section.label}
																</button>
															);
														})}
												</div>
											)}
											{/* Duration group */}
											{availableSections.some(s => s.group === 'durationRestrictions') && (
												<div>
													{availableSections.some(s => s.group === 'general' || s.group === 'timeDate') && <div className="-mx-1 my-1 h-px bg-border" />}
													<div className="px-2 py-1.5 text-sm font-medium">{t('conditionEditor.groupDurationRestrictions')}</div>
													{availableSections
														.filter(s => s.group === 'durationRestrictions')
														.map(section => {
															const Icon = section.icon;
															return (
																<button
																	key={section.id}
																	type="button"
																	className="flex items-center w-full gap-2 px-2 py-1.5 text-sm rounded-sm cursor-default select-none hover:bg-accent hover:text-accent-foreground"
																	onClick={() => addSection(section.id)}
																>
																	<Icon className="w-4 h-4" />
																	{section.label}
																</button>
															);
														})}
												</div>
											)}
											{/* Restrictions group */}
											{availableSections.some(s => s.group === 'restrictions') && (
												<div>
													{availableSections.some(s => s.group === 'general' || s.group === 'timeDate' || s.group === 'durationRestrictions') && <div className="-mx-1 my-1 h-px bg-border" />}
													<div className="px-2 py-1.5 text-sm font-medium">{t('conditionEditor.groupRestrictions')}</div>
													{availableSections
														.filter(s => s.group === 'restrictions')
														.map(section => {
															const Icon = section.icon;
															return (
																<button
																	key={section.id}
																	type="button"
																	className="flex items-center w-full gap-2 px-2 py-1.5 text-sm rounded-sm cursor-default select-none hover:bg-accent hover:text-accent-foreground"
																	onClick={() => addSection(section.id)}
																>
																	<Icon className="w-4 h-4" />
																	{section.label}
																</button>
															);
														})}
												</div>
											)}
										</div>
									)}
								</div>
							)}
							{addedSections.length > 0 && (
								<Button
									type="button"
									variant="ghost"
									size="sm"
									className="font-normal"
									onClick={toggleCollapseAll}
								>
									<ChevronsUpDown className="w-4 h-4 mr-2" />
									{allCollapsed
										? t('conditionEditor.expandAll')
										: t('conditionEditor.collapseAll')}
								</Button>
							)}
						</div>
						<div className="flex items-center gap-2">
							<Button type="submit">
								<Save className="w-4 h-4 mr-2" />
								{mode === 'create'
									? t('conditionEditor.saveRules')
									: t('conditionEditor.updateRules')}
							</Button>
							<Button
								type="button"
								variant="outline"
								onClick={onCancel}
							>
								{t('conditionEditor.cancel')}
							</Button>
						</div>
					</div>
				</form>
			</DialogContent>
		</Dialog>
	);
};
